import { unknownEntries, isUnknownArray, isRecord } from "../types/data";
import type { Rating } from "../types/rating";
import { getMediaType } from "./gameProgressEligibility";
import { getCountryCode } from "./countryNames";
import {
  getPrimaryStatisticsRating,
  getStatisticsMediaType
} from "./mediaStatistics";

const asArray = <T>(value: T | T[] | null | undefined): T[] => {
  if (isUnknownArray(value)) return value;
  return value == null || value === "" ? [] : [value];
};

const normalizeLabel = (value: unknown) => {
  if (isRecord(value)) {
    return String(
      value.name || value.iso_3166_1 || value.english_name || ""
    ).trim();
  }
  return String(value || "").trim();
};

export const PUBLIC_PROFILE_STATS_VERSION = 2;
export const PUBLIC_PROFILE_EXPERIENCE_SUMMARY_VERSION = 1;
// Version 3 deliberately stops recording provisional current-week quality as
// earned XP. Closed-week snapshots are the only source of that reward.
export const PUBLIC_PROFILE_XP_EVENT_LEDGER_VERSION = 3;
// Profiles below version 1 may still contain a provisional current-week
// quality bonus or a pre-ledger achievement reward paid a second time.
// Version 2 also performs a one-time durable journal backfill for post-rollout
// weekly-streak rewards. Version 3 seeds missing historical component markers
// without paying rewards that are already present in the aggregate XP total.
export const PUBLIC_PROFILE_EXPERIENCE_REPAIR_VERSION = 3;

export const buildRatingShareStats = (ratings: Rating[] = []) => {
  const countryNames = new Set<string>();
  const genreCounts = new Map<string, { name: string; count: number }>();
  const userRates: number[] = [];
  const groupedRatings = new Map<string, Rating[]>();

  (isUnknownArray(ratings) ? ratings : [])
    .filter((rating: Rating) => rating && rating.active !== false)
    .forEach((rating: Rating, index) => {
      const filmId = rating.filmId ?? rating.id;
      const key = filmId == null || String(filmId).trim() === ""
        ? `anonymous:${index}`
        : `${getMediaType(rating)}:${String(filmId).trim()}`;
      const group = groupedRatings.get(key) || [];
      group.push(rating);
      groupedRatings.set(key, group);
    });

  groupedRatings.forEach((group) => {
    const ratingGenres = new Map<string, string>();

    group.forEach((rating: Rating = {}) => {
      [
        ...asArray(rating.originCountry),
        ...asArray(rating.productionCountry),
        ...asArray(rating.productionCountries),
        ...asArray(rating.importedCountries)
      ].forEach((country) => {
        const name = normalizeLabel(country);
        const code = getCountryCode(country);
        if (code || name) {
          countryNames.add(code || name.toLocaleLowerCase());
        }
      });

      [
        ...asArray(rating.genres),
        ...asArray(rating.filmGenres),
        ...asArray(rating.importedGenres)
      ].forEach((genre) => {
        const name = normalizeLabel(genre);
        if (name) ratingGenres.set(name.toLocaleLowerCase(), name);
      });
    });

    ratingGenres.forEach((name, key) => {
      const current = genreCounts.get(key) || { name, count: 0 };
      current.count += 1;
      genreCounts.set(key, current);
    });

    const rate = getPrimaryStatisticsRating(group[group.length - 1]);
    if (rate != null && Number.isFinite(rate) && rate > 0 && rate <= 10) {
      userRates.push(rate);
    }
  });

  const favoriteGenre = [...genreCounts.values()]
    .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name))[0]
    ?.name || "";
  const averageRating = userRates.length
    ? Math.round(
      (userRates.reduce((sum, value) => sum + value, 0) / userRates.length) * 10
    ) / 10
    : null;
  const watchedMovies = [...groupedRatings.values()].filter((group) =>
    getStatisticsMediaType(group[group.length - 1]) === "movie"
  ).length;
  const watchedSeries = [...groupedRatings.values()].filter((group) =>
    getStatisticsMediaType(group[group.length - 1]) === "tv"
  ).length;

  return {
    watchedFilms: groupedRatings.size,
    watchedMovies,
    watchedSeries,
    countriesCount: countryNames.size,
    favoriteGenre,
    averageRating
  };
};

export const arePublicStatsEqual = (stored: object = {}, next: object = {}) =>
  unknownEntries(next).every(([key, value]) => {
    const current: unknown = (stored as Record<string, unknown>)?.[key];
    if (value == null) return current == null;
    if (typeof value === "number") return Number(current) === value;
    if (isUnknownArray(value) || typeof value === "object") {
      return JSON.stringify(current ?? null) === JSON.stringify(value);
    }
    return String(current ?? "") === String(value);
  });
